import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { RateSource } from '@/modules/common/types/rate-source';
import {
  ProjectTeamMember,
  ProjectTeamMemberType,
} from '@/modules/projects/types/project-team-member';
import { getTeamMembersWithRates } from '@/modules/team/api/team.api';
import { logger } from '@/utils/logger/logger';

const TAG = 'UseRateOverviewTableManagement:';

interface UseRateOverviewTableManagementProps {
  projectData?: ProjectTeamMember[];
  draftData?: {
    selectedTeamMembers?: ProjectTeamMember[];
    cachedTeamMembers?: ProjectTeamMember[];
  };
  currentRateSource: RateSource;
  defaultRateSource?: RateSource;
  flatRateValue?: number;
  currency?: string;
}

interface UseRateOverviewTableManagementReturn {
  teamMembers: ProjectTeamMember[];
  cachedTeamMembers: ProjectTeamMember[];
  setTeamMembers: Dispatch<SetStateAction<ProjectTeamMember[]>>;
  loadMembers: (
    searchString: string | undefined,
    cursor: number | null | undefined
  ) => Promise<any>;
  handleAddSelectedTeamMember: (item: DropdownSearchItem) => void;
  handleRateSourceChange: (memberId: string, newRateSource: RateSource) => void;
  handleCustomRateChange: (memberId: string, newRate: number) => void;
  handleRemoveMember: (memberId: string) => void;
  isRefetchingRates: boolean;
  rateRefetchError: Error | null;
}

export function useRateOverviewTableManagement({
  projectData,
  draftData,
  currentRateSource,
  defaultRateSource,
  flatRateValue,
  currency,
}: UseRateOverviewTableManagementProps): UseRateOverviewTableManagementReturn {
  const [teamMembers, setTeamMembers] = useState<ProjectTeamMember[]>(() => {
    // In create mode with draft, use draft data; otherwise use project data
    if (draftData?.selectedTeamMembers && draftData.selectedTeamMembers.length > 0) {
      return draftData.selectedTeamMembers;
    }
    return projectData || [];
  });

  const [cachedTeamMembers, setCachedTeamMembers] = useState<ProjectTeamMember[]>(() => {
    return draftData?.cachedTeamMembers || [];
  });

  const [isRefetchingRates, setIsRefetchingRates] = useState(false);
  const [rateRefetchError, setRateRefetchError] = useState<Error | null>(null);
  const previousCurrencyRef = useRef<string | undefined>(currency);

  // Store original individual rate sources that never get overwritten by project-level changes
  const [originalIndividualRateSources] = useState<Map<string, RateSource>>(() => {
    const map = new Map<string, RateSource>();

    // Initialize with project data and cached team members' original activeRateSource
    [...(projectData || []), ...cachedTeamMembers].forEach(member => {
      if (member.activeRateSource && !map.has(member.id)) {
        map.set(member.id, member.activeRateSource);
      }
    });

    return map;
  });

  // Track the previous rate source (the one used right before switching to ProjectRateCard)
  const [previousRateSource, setPreviousRateSource] = useState<RateSource | undefined>(undefined);

  // Update team members when projectData changes
  useEffect(() => {
    if (projectData && projectData.length > 0) {
      setTeamMembers([...projectData]);
    }
  }, [projectData]);

  // Update team members' activeRateSource when project rate source changes
  useEffect(() => {
    setTeamMembers(prev =>
      prev.map(member => {
        if (currentRateSource === RateSource.ProjectRateCard) {
          // When switching to ProjectRateCard, use the previous rate source
          if (previousRateSource === RateSource.FlatRate) {
            // If previous was FlatRate, set to Custom with flat rate value
            return {
              ...member,
              activeRateSource: RateSource.Custom,
              customRateValue: flatRateValue ?? 0,
            };
          } else if (previousRateSource) {
            // If previous was any other source, use that source
            return {
              ...member,
              activeRateSource: previousRateSource,
            };
          } else {
            // Fallback: use original individual rate source or default
            const originalRateSource = originalIndividualRateSources.get(member.id);
            return {
              ...member,
              activeRateSource:
                originalRateSource || defaultRateSource || RateSource.UserExternalRate,
            };
          }
        } else {
          // When NOT using ProjectRateCard, all members use the project-level rate source
          return {
            ...member,
            activeRateSource: currentRateSource,
          };
        }
      })
    );

    // Track the current rate source as previous for next transition
    if (currentRateSource !== RateSource.ProjectRateCard) {
      setPreviousRateSource(currentRateSource);
    }
  }, [
    currentRateSource,
    defaultRateSource,
    originalIndividualRateSources,
    previousRateSource,
    flatRateValue,
  ]);

  // Dynamically update Custom rate values when flat rate changes (only if in ProjectRateCard mode and previous was FlatRate)
  useEffect(() => {
    if (
      currentRateSource === RateSource.ProjectRateCard &&
      previousRateSource === RateSource.FlatRate
    ) {
      setTeamMembers(prev =>
        prev.map(member => {
          // Only update members that have Custom rate source
          if (member.activeRateSource === RateSource.Custom) {
            return {
              ...member,
              customRateValue: flatRateValue ?? 0,
            };
          }
          return member;
        })
      );
    }
  }, [flatRateValue, currentRateSource, previousRateSource]);

  /**
   * Convert ProjectTeamMember to DropdownSearchItem for dropdown display
   */
  const convertToDropdownItem = useCallback(
    (member: ProjectTeamMember): DropdownSearchItem => ({
      id: member.id,
      name: member.name,
      avatarLink: member.avatarLink,
      secondaryText: member.department,
    }),
    []
  );

  /**
   * Common logic for processing team member results - caches data and converts for dropdown
   */
  const processTeamMemberResults = useCallback(
    (result: any) => {
      // Cache all team members (full ProjectTeamMember objects)
      // IMPORTANT: Always update existing cached members to ensure fresh rates when currency changes
      setCachedTeamMembers(prev => {
        const prevMap = new Map(prev.map(member => [member.id, member]));
        const updatedMap = new Map(prevMap);

        // Update or add each member from the result
        result.items.forEach((member: ProjectTeamMember) => {
          updatedMap.set(member.id, member);
        });

        return Array.from(updatedMap.values());
      });

      // Convert to DropdownSearchItem for dropdown display
      const dropdownItems = result.items.map(convertToDropdownItem);

      // Filter out existing team members by ID
      return {
        ...result,
        items: dropdownItems.filter(item => {
          const existingMemberIds = new Set(teamMembers.map(member => member.id));
          return !existingMemberIds.has(item.id);
        }),
      };
    },
    [convertToDropdownItem, teamMembers]
  );

  /**
   * Wrapper for getTeamMembersWithRates that filters out existing team members and caches results.
   * Supports both loading all members and searching by name.
   */
  const loadMembers = useCallback(
    async (searchString: string | undefined, cursor: number | null | undefined) => {
      const result = await getTeamMembersWithRates(searchString, cursor, currency);
      return processTeamMemberResults(result);
    },
    [processTeamMemberResults, currency]
  );

  /**
   * Handle adding a new team member to the team
   */
  const handleAddSelectedTeamMember = useCallback(
    (item: DropdownSearchItem) => {
      // Get the cached full ProjectTeamMember data from local cache
      const cachedMember = cachedTeamMembers.find(member => member.id === item.id);

      if (!cachedMember) {
        logger.error(TAG, 'Team member not found in cache:', item.id);
        return;
      }

      // Store the original individual rate source if we don't have it yet
      if (cachedMember.activeRateSource && !originalIndividualRateSources.has(cachedMember.id)) {
        originalIndividualRateSources.set(cachedMember.id, cachedMember.activeRateSource);
      }

      // Create team member from cached full data
      const newMember: ProjectTeamMember = {
        ...cachedMember, // Use all the cached data including rate values
        type: ProjectTeamMemberType.ManuallyAdded,
        activeRateSource:
          currentRateSource === RateSource.ProjectRateCard
            ? cachedMember.activeRateSource || defaultRateSource || RateSource.UserExternalRate
            : currentRateSource,
      };

      setTeamMembers(prev => [...prev, newMember]);
    },
    [cachedTeamMembers, currentRateSource, defaultRateSource, originalIndividualRateSources]
  );

  /**
   * Handle rate source change for a team member
   */
  const handleRateSourceChange = useCallback((memberId: string, newRateSource: RateSource) => {
    setTeamMembers(prev =>
      prev.map(member => {
        if (member.id === memberId) {
          return { ...member, activeRateSource: newRateSource };
        }
        return member;
      })
    );
  }, []);

  /**
   * Handle custom rate change - sets custom rate value and rate source
   */
  const handleCustomRateChange = useCallback((memberId: string, newRate: number) => {
    setTeamMembers(prev =>
      prev.map(member =>
        member.id === memberId
          ? { ...member, customRateValue: newRate, activeRateSource: RateSource.Custom }
          : member
      )
    );
  }, []);

  /**
   * Handle removing a manually added team member
   */
  const handleRemoveMember = useCallback((memberId: string) => {
    setTeamMembers(prev => prev.filter(member => member.id !== memberId));
  }, []);

  /**
   * Refetch team member rates when currency changes.
   * Preserves custom activeRateSource and customRateValue for each member.
   */
  useEffect(() => {
    const refetchRatesForCurrency = async () => {
      // Skip on initial mount
      if (previousCurrencyRef.current === undefined) {
        previousCurrencyRef.current = currency;
        return;
      }

      // Skip if currency hasn't changed
      if (previousCurrencyRef.current === currency) {
        return;
      }

      // Skip if no team members to refetch
      if (teamMembers.length === 0) {
        previousCurrencyRef.current = currency;
        return;
      }

      try {
        setIsRefetchingRates(true);
        setRateRefetchError(null);

        // Extract user IDs from current team members
        const userIds = teamMembers.map(member => Number(member.id));

        logger.debug(TAG, 'Refetching team member rates with new currency', {
          userIds,
          currency,
          teamMemberCount: teamMembers.length,
        });

        // Fetch specific team members with new currency (single API call)
        const result = await getTeamMembersWithRates(undefined, undefined, currency, userIds);

        logger.debug(TAG, 'Refetched team members with new currency', {
          refetchedCount: result.items.length,
        });

        // Create lookup map for refetched data
        const refetchedMap = new Map(result.items.map(member => [member.id, member]));

        // Update team members: merge refetched rates while preserving custom changes
        setTeamMembers(prev =>
          prev.map(member => {
            const refetchedMember = refetchedMap.get(member.id);

            if (!refetchedMember) {
              logger.warn(TAG, 'Team member not found in refetch', { memberId: member.id });
              return member;
            }

            // Preserve custom changes (activeRateSource, customRateValue)
            // Update all rate values from refetched data
            return {
              ...member,
              userExternalRateValue: refetchedMember.userExternalRateValue,
              seniorityBasedRateValue: refetchedMember.seniorityBasedRateValue,
              roleBasedRateValue: refetchedMember.roleBasedRateValue,
              globalDefaultRateValue: refetchedMember.globalDefaultRateValue,
              // activeRateSource and customRateValue are preserved from current state
            };
          })
        );

        // Update cached members as well
        setCachedTeamMembers(prev =>
          prev.map(member => {
            const refetchedMember = refetchedMap.get(member.id);
            return refetchedMember
              ? {
                  ...member,
                  userExternalRateValue: refetchedMember.userExternalRateValue,
                  seniorityBasedRateValue: refetchedMember.seniorityBasedRateValue,
                  roleBasedRateValue: refetchedMember.roleBasedRateValue,
                  globalDefaultRateValue: refetchedMember.globalDefaultRateValue,
                }
              : member;
          })
        );

        previousCurrencyRef.current = currency;
      } catch (error) {
        logger.error(TAG, 'Failed to refetch rates for currency change', error);
        setRateRefetchError(error as Error);
      } finally {
        setIsRefetchingRates(false);
      }
    };

    refetchRatesForCurrency();
  }, [currency, teamMembers.length]);

  return {
    teamMembers,
    cachedTeamMembers,
    setTeamMembers,
    loadMembers,
    handleAddSelectedTeamMember,
    handleRateSourceChange,
    handleCustomRateChange,
    handleRemoveMember,
    isRefetchingRates,
    rateRefetchError,
  };
}
